import { supabase } from "@/integrations/supabase/client";

/**
 * Lightweight, anonymous performance telemetry.
 *
 * Records Core Web Vitals and route-change timings so slow pages and
 * regressions show up in the admin dashboard. No cookies, no identifiers and
 * no personal data are collected — only the route path, a metric name, a
 * duration and a coarse device bucket.
 */

type Metric = "LCP" | "CLS" | "TTFB" | "FCP" | "INP" | "route_change";

interface Sample {
  route: string;
  metric: Metric;
  value: number;
  device: string;
  nav_type: string;
}

const SAMPLE_RATE = 0.25; // only a quarter of sessions report, to keep traffic tiny
const queue: Sample[] = [];
let started = false;
let sessionIncluded = false;

function device() {
  const w = window.innerWidth;
  return w < 640 ? "mobile" : w < 1024 ? "tablet" : "desktop";
}

/** Collapses dynamic segments so /blog/some-post groups under /blog/:slug. */
export function normaliseRoute(pathname: string) {
  return pathname
    .replace(/\/blog\/[^/]+$/, "/blog/:slug")
    .replace(/\/services\/[^/]+$/, (m) =>
      m === "/services/fractional-cfo-finance-director" ? m : "/services/:slug",
    )
    .replace(/\/areas\/[^/]+$/, (m) => m)
    .slice(0, 300) || "/";
}

function push(metric: Metric, value: number, navType: string, route = window.location.pathname) {
  if (!sessionIncluded || !Number.isFinite(value) || value < 0) return;
  queue.push({
    route: normaliseRoute(route),
    metric,
    value: Math.round(value * 1000) / 1000,
    device: device(),
    nav_type: navType,
  });
  if (queue.length >= 12) void flush();
}

async function flush() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  try {
    await supabase.from("perf_samples").insert(batch);
  } catch {
    /* telemetry must never break the page */
  }
}

/** Called by the router on every client-side navigation. */
export function recordRouteChange(route: string, durationMs: number) {
  push("route_change", durationMs, "spa", route);
}

export function initPerfTracking() {
  if (started || typeof window === "undefined") return;
  started = true;
  sessionIncluded = Math.random() < SAMPLE_RATE;
  if (!sessionIncluded) return;

  const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  if (nav) push("TTFB", nav.responseStart, "load");

  const observe = (type: string, cb: (entries: PerformanceEntryList) => void) => {
    try {
      const po = new PerformanceObserver((list) => cb(list.getEntries()));
      po.observe({ type, buffered: true } as PerformanceObserverInit);
      return po;
    } catch {
      return null;
    }
  };

  observe("paint", (entries) => {
    const fcp = entries.find((e) => e.name === "first-contentful-paint");
    if (fcp) push("FCP", fcp.startTime, "load");
  });

  let lcp = 0;
  observe("largest-contentful-paint", (entries) => {
    const last = entries[entries.length - 1];
    if (last) lcp = last.startTime;
  });

  let cls = 0;
  observe("layout-shift", (entries) => {
    for (const e of entries as unknown as { value: number; hadRecentInput: boolean }[]) {
      if (!e.hadRecentInput) cls += e.value;
    }
  });

  let inp = 0;
  observe("event", (entries) => {
    for (const e of entries as unknown as { duration: number; interactionId?: number }[]) {
      if (e.interactionId && e.duration > inp) inp = e.duration;
    }
  });

  const finalise = () => {
    if (lcp) push("LCP", lcp, "load");
    push("CLS", cls * 1000, "load"); // stored x1000 so the numeric column keeps precision
    if (inp) push("INP", inp, "load");
    lcp = 0;
    inp = 0;
    void flush();
  };

  addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") finalise();
  });
  addEventListener("pagehide", finalise);
}
